import { useState, useEffect } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Calendar } from '../components/ui/calendar';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Plus, MoreHorizontal, Pencil, Trash2, Loader2, Search, Building2, CalendarDays, ListChecks, ToggleLeft, ToggleRight, UserCog } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DAYS_OF_WEEK = [
  { value: 0, label: 'Mon' },
  { value: 1, label: 'Tue' },
  { value: 2, label: 'Wed' },
  { value: 3, label: 'Thu' },
  { value: 4, label: 'Fri' },
  { value: 5, label: 'Sat' },
  { value: 6, label: 'Sun' },
];

export default function ClientsPage() {
  const { user } = useAuth();
  const isAdmin = user?.roles?.includes('ADMIN');
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: '',
    default_working_days: [0, 1, 2, 3, 4],
    allow_weekend_work: false,
    allow_holiday_work: false,
  });

  // Task management
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [taskClient, setTaskClient] = useState(null);
  const [clientTasks, setClientTasks] = useState([]);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [newTaskName, setNewTaskName] = useState('');
  const [newTaskCode, setNewTaskCode] = useState('');
  const [newTaskCategory, setNewTaskCategory] = useState('');
  const [savingTask, setSavingTask] = useState(false);
  const [editingTaskId, setEditingTaskId] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', code: '', category: '' });

  // Holiday management
  const [selectedClient, setSelectedClient] = useState(null);
  const [holidays, setHolidays] = useState([]);
  const [holidayDialogOpen, setHolidayDialogOpen] = useState(false);
  const [holidayDateStr, setHolidayDateStr] = useState('');
  const [holidayName, setHolidayName] = useState('');
  const [holidayType, setHolidayType] = useState('HOLIDAY');
  const [selectedDate, setSelectedDate] = useState(null); // For calendar visual selection

  // Manager assignment
  const [clientManagers, setClientManagers] = useState([]); // users with CLIENT_MANAGER role
  const [assignManagerDialogOpen, setAssignManagerDialogOpen] = useState(false);
  const [assignManagerClient, setAssignManagerClient] = useState(null);
  const [selectedManagerId, setSelectedManagerId] = useState('__none__');
  const [savingManager, setSavingManager] = useState(false);

  useEffect(() => {
    fetchClients();
    fetchClientManagers();
  }, []);

  // Map: client_id -> manager user (built from clientManagers list)
  const clientManagerMap = clientManagers.reduce((acc, u) => {
    (u.managed_client_ids || []).forEach(cid => { acc[cid] = u; });
    return acc;
  }, {});

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/clients`);
      setClients(response.data);
    } catch (error) {
      toast.error('Failed to load clients');
    } finally {
      setLoading(false);
    }
  };

  const fetchClientManagers = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/users`);
      setClientManagers(response.data.filter(u => (u.roles || []).includes('CLIENT_MANAGER')));
    } catch {
      // silently ignore — only ADMIN/MANAGER can fetch users
    }
  };

  const openAssignManagerDialog = (client) => {
    setAssignManagerClient(client);
    const currentManager = clientManagerMap[client.id];
    setSelectedManagerId(currentManager?.id || '__none__');
    setAssignManagerDialogOpen(true);
  };

  const handleAssignManager = async () => {
    setSavingManager(true);
    try {
      await axios.post(`${API_URL}/api/clients/${assignManagerClient.id}/assign-manager`, {
        user_id: selectedManagerId === '__none__' ? null : selectedManagerId,
      });
      toast.success(selectedManagerId !== '__none__' ? 'Manager assigned' : 'Manager unassigned');
      await fetchClientManagers();
      setAssignManagerDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign manager');
    } finally {
      setSavingManager(false);
    }
  };

  const openTaskDialog = (client) => {
    setTaskClient(client);
    setNewTaskName('');
    setNewTaskCode('');
    setNewTaskCategory('');
    setTaskDialogOpen(true);
    fetchClientTasks(client.id);
  };

  const fetchClientTasks = async (clientId) => {
    setTasksLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/clients/${clientId}/tasks`);
      setClientTasks(response.data);
    } catch {
      toast.error('Failed to load tasks');
    } finally {
      setTasksLoading(false);
    }
  };

  const handleAddClientTask = async () => {
    if (!newTaskName.trim()) {
      toast.error('Task name is required');
      return;
    }
    setSavingTask(true);
    try {
      await axios.post(`${API_URL}/api/clients/${taskClient.id}/tasks`, {
        name: newTaskName.trim(),
        code: newTaskCode.trim(),
        category: newTaskCategory || null,
      });
      setNewTaskName('');
      setNewTaskCode('');
      setNewTaskCategory('');
      await fetchClientTasks(taskClient.id);
      toast.success('Task added');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add task');
    } finally {
      setSavingTask(false);
    }
  };

  const handleToggleClientTask = async (task) => {
    try {
      await axios.put(`${API_URL}/api/clients/${taskClient.id}/tasks/${task.id}`, {
        is_active: !task.is_active,
      });
      await fetchClientTasks(taskClient.id);
    } catch {
      toast.error('Failed to update task');
    }
  };

  const handleDeleteClientTask = async (task) => {
    if (task.is_system) {
      toast.error("The 'Other' task cannot be deleted");
      return;
    }
    try {
      await axios.delete(`${API_URL}/api/clients/${taskClient.id}/tasks/${task.id}`);
      await fetchClientTasks(taskClient.id);
      toast.success('Task deleted');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete task');
    }
  };

  const startEditTask = (task) => {
    setEditingTaskId(task.id);
    setEditForm({ name: task.name, code: task.code || '', category: task.category || '' });
  };

  const handleSaveTaskEdit = async (task) => {
    if (!editForm.name.trim()) { toast.error('Task name is required'); return; }
    try {
      await axios.put(`${API_URL}/api/clients/${taskClient.id}/tasks/${task.id}`, {
        name: editForm.name.trim(),
        code: editForm.code.trim(),
        category: (editForm.category && editForm.category !== '__none__') ? editForm.category : null,
      });
      setEditingTaskId(null);
      await fetchClientTasks(taskClient.id);
      toast.success('Task updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update task');
    }
  };

  const fetchHolidays = async (clientId) => {
    try {
      const response = await axios.get(`${API_URL}/api/clients/${clientId}/holidays`);
      setHolidays(response.data);
    } catch (error) {
      toast.error('Failed to load holidays');
    }
  };

  const openCreateDialog = () => {
    setEditingClient(null);
    setFormData({
      name: '',
      code: '',
      description: '',
      default_working_days: [0, 1, 2, 3, 4],
      allow_weekend_work: false,
      allow_holiday_work: false,
    });
    setDialogOpen(true);
  };

  const openEditDialog = (client) => {
    setEditingClient(client);
    setFormData({
      name: client.name,
      code: client.code,
      description: client.description || '',
      default_working_days: client.default_working_days || [0, 1, 2, 3, 4],
      allow_weekend_work: client.allow_weekend_work || false,
      allow_holiday_work: client.allow_holiday_work || false,
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      if (editingClient) {
        await axios.put(`${API_URL}/api/clients/${editingClient.id}`, formData);
        toast.success('Client updated successfully');
        setDialogOpen(false);
        fetchClients();
      } else {
        const response = await axios.post(`${API_URL}/api/clients`, formData);
        toast.success('Client created! Now add holidays using the calendar on the right.');
        setDialogOpen(false);
        await fetchClients();
        // Auto-select the new client to show calendar
        setSelectedClient(response.data);
        fetchHolidays(response.data.id);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (client) => {
    if (!window.confirm(`Are you sure you want to delete "${client.name}"?`)) return;

    try {
      await axios.delete(`${API_URL}/api/clients/${client.id}`);
      toast.success('Client deleted successfully');
      fetchClients();
      if (selectedClient?.id === client.id) {
        setSelectedClient(null);
        setHolidays([]);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete client');
    }
  };

  const selectClientForCalendar = (client) => {
    setSelectedClient(client);
    fetchHolidays(client.id);
  };

  const handleAddHoliday = async () => {
    if (!holidayDateStr || !holidayName.trim()) {
      toast.error('Please select a date and enter a name');
      return;
    }

    try {
      await axios.post(`${API_URL}/api/clients/${selectedClient.id}/holidays`, {
        client_id: selectedClient.id,
        date: holidayDateStr,
        name: holidayName,
        holiday_type: holidayType,
      });
      toast.success('Holiday added');
      fetchHolidays(selectedClient.id);
      setHolidayDialogOpen(false);
      setHolidayDateStr('');
      setHolidayName('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add holiday');
    }
  };

  const handleDeleteHoliday = async (holiday) => {
    try {
      await axios.delete(`${API_URL}/api/clients/${selectedClient.id}/holidays/${holiday.id}`);
      toast.success('Holiday deleted');
      fetchHolidays(selectedClient.id);
    } catch (error) {
      toast.error('Failed to delete holiday');
    }
  };

  const toggleWorkingDay = (day) => {
    setFormData(prev => ({
      ...prev,
      default_working_days: prev.default_working_days.includes(day)
        ? prev.default_working_days.filter(d => d !== day)
        : [...prev.default_working_days, day].sort()
    }));
  };

  const filteredClients = clients.filter(
    (client) =>
      client.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      client.code.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const holidayDates = holidays.map(h => {
    // Parse date string to avoid timezone issues
    const [year, month, day] = h.date.split('-').map(Number);
    return new Date(year, month - 1, day);
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Clients</h1>
          <p className="text-muted-foreground">Manage clients and their working calendars</p>
        </div>
        {isAdmin && (
          <Button onClick={openCreateDialog} data-testid="add-client-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add Client
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Clients List */}
        <Card>
          <CardHeader className="pb-4">
            <div className="flex items-center gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search clients..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                  data-testid="search-clients-input"
                />
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Client</TableHead>
                  <TableHead>Manager</TableHead>
                  <TableHead>Working Days</TableHead>
                  <TableHead className="w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredClients.map((client) => (
                  <TableRow 
                    key={client.id} 
                    className={`cursor-pointer ${selectedClient?.id === client.id ? 'bg-primary/10' : ''}`}
                    onClick={() => selectClientForCalendar(client)}
                    data-testid={`client-row-${client.id}`}
                  >
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                          <Building2 className="w-4 h-4 text-primary" />
                        </div>
                        <div>
                          <div className="font-medium">{client.name}</div>
                          <div className="text-xs text-muted-foreground">{client.code}</div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      {clientManagerMap[client.id] ? (
                        <Badge variant="outline" className="bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 text-xs">
                          {clientManagerMap[client.id].name}
                        </Badge>
                      ) : (
                        <span className="text-xs text-muted-foreground">Unassigned</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        {DAYS_OF_WEEK.map(day => (
                          <span
                            key={day.value}
                            className={`text-xs px-1.5 py-0.5 rounded ${
                              client.default_working_days?.includes(day.value)
                                ? 'bg-primary/20 text-primary'
                                : 'bg-muted text-muted-foreground'
                            }`}
                          >
                            {day.label[0]}
                          </span>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" data-testid={`client-menu-${client.id}`}>
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openEditDialog(client)}>
                            <Pencil className="w-4 h-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => openTaskDialog(client)}>
                            <ListChecks className="w-4 h-4 mr-2" />
                            Manage Tasks
                          </DropdownMenuItem>
                          {isAdmin && (
                            <DropdownMenuItem onClick={() => openAssignManagerDialog(client)}>
                              <UserCog className="w-4 h-4 mr-2" />
                              Assign Manager
                            </DropdownMenuItem>
                          )}
                          {isAdmin && (
                            <DropdownMenuItem onClick={() => handleDelete(client)} className="text-destructive">
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
                {filteredClients.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                      No clients found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Calendar Panel */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CalendarDays className="w-5 h-5" />
              {selectedClient ? `${selectedClient.name} Calendar` : 'Select a Client'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {selectedClient ? (
              <div className="space-y-4">
                <div className="flex justify-center">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={setSelectedDate}
                    modifiers={{ holiday: holidayDates }}
                    modifiersStyles={{
                      holiday: { backgroundColor: 'hsl(var(--destructive))', color: 'white', borderRadius: '50%' }
                    }}
                    className="rounded-md border"
                  />
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">
                    {holidays.length} holiday{holidays.length !== 1 ? 's' : ''} configured
                  </span>
                  <Button size="sm" onClick={() => setHolidayDialogOpen(true)} data-testid="add-holiday-btn">
                    <Plus className="w-4 h-4 mr-1" />
                    Add Holiday
                  </Button>
                </div>

                {/* Holidays List */}
                <div className="max-h-48 overflow-y-auto space-y-2">
                  {holidays.map((holiday) => (
                    <div
                      key={holiday.id}
                      className="flex items-center justify-between p-2 bg-muted rounded-md"
                    >
                      <div>
                        <span className="font-medium text-sm">{holiday.name}</span>
                        <span className="text-xs text-muted-foreground ml-2">
                          {format(new Date(holiday.date), 'MMM d, yyyy')}
                        </span>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        onClick={() => handleDeleteHoliday(holiday)}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                <CalendarDays className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Select a client to view and manage their calendar</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Create/Edit Client Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingClient ? 'Edit Client' : 'Create Client'}</DialogTitle>
            <DialogDescription>
              {editingClient ? 'Update client information' : 'Add a new client'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                data-testid="client-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="code">Code</Label>
              <Input
                id="code"
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                required
                placeholder="e.g., ACME"
                data-testid="client-code-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={2}
                data-testid="client-description-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Default Working Days</Label>
              <div className="flex flex-wrap gap-2">
                {DAYS_OF_WEEK.map((day) => (
                  <label
                    key={day.value}
                    className={`flex items-center gap-2 px-3 py-2 rounded-md border cursor-pointer transition-colors ${
                      formData.default_working_days.includes(day.value)
                        ? 'bg-primary/10 border-primary'
                        : 'bg-background border-border hover:bg-muted'
                    }`}
                  >
                    <Checkbox
                      checked={formData.default_working_days.includes(day.value)}
                      onCheckedChange={() => toggleWorkingDay(day.value)}
                    />
                    <span className="text-sm">{day.label}</span>
                  </label>
                ))}
              </div>
            </div>
            
            {/* Work Permission Options */}
            <div className="space-y-3 pt-2 border-t">
              <Label className="text-sm font-medium">Time Entry Permissions</Label>
              <div className="flex items-center space-x-3">
                <Checkbox
                  id="allow_weekend_work"
                  checked={formData.allow_weekend_work}
                  onCheckedChange={(checked) => setFormData({ ...formData, allow_weekend_work: checked })}
                  data-testid="allow-weekend-work-checkbox"
                />
                <label htmlFor="allow_weekend_work" className="text-sm cursor-pointer">
                  Allow employees to log time on <span className="font-medium text-orange-600">weekends</span>
                </label>
              </div>
              <div className="flex items-center space-x-3">
                <Checkbox
                  id="allow_holiday_work"
                  checked={formData.allow_holiday_work}
                  onCheckedChange={(checked) => setFormData({ ...formData, allow_holiday_work: checked })}
                  data-testid="allow-holiday-work-checkbox"
                />
                <label htmlFor="allow_holiday_work" className="text-sm cursor-pointer">
                  Allow employees to log time on <span className="font-medium text-red-600">holidays</span>
                </label>
              </div>
              <p className="text-xs text-muted-foreground">
                {editingClient 
                  ? "To manage holidays, save and use the calendar panel on the right."
                  : "After creating, select the client to add holidays and non-working days."
                }
              </p>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} data-testid="save-client-btn">
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  editingClient ? 'Update' : 'Create'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Manage Tasks Dialog */}
      <Dialog open={taskDialogOpen} onOpenChange={setTaskDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ListChecks className="w-5 h-5" />
              Tasks for {taskClient?.name}
            </DialogTitle>
            <DialogDescription>
              Define the tasks for this client. Projects assigned to this client can then select which tasks apply.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Add task form */}
            <div className="flex gap-2">
              <Input
                placeholder="Task name (e.g., Design)"
                value={newTaskName}
                onChange={(e) => setNewTaskName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAddClientTask()}
                className="flex-1"
              />
              <Input
                placeholder="Code"
                value={newTaskCode}
                onChange={(e) => setNewTaskCode(e.target.value.toUpperCase())}
                className="w-20"
              />
              <Select value={newTaskCategory} onValueChange={setNewTaskCategory}>
                <SelectTrigger className="w-24">
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">None</SelectItem>
                  <SelectItem value="CAPS">CAPS</SelectItem>
                  <SelectItem value="OPS">OPS</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={handleAddClientTask} disabled={savingTask} size="sm">
                {savingTask ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              </Button>
            </div>
            {/* Task list */}
            {tasksLoading ? (
              <div className="flex justify-center py-6">
                <Loader2 className="w-6 h-6 animate-spin text-primary" />
              </div>
            ) : clientTasks.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <ListChecks className="w-10 h-10 mx-auto mb-3 opacity-40" />
                <p className="text-sm">No tasks yet. Add your first task above.</p>
                <p className="text-xs mt-1">An "Other" catch-all task will be auto-created with your first task.</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {clientTasks.map((task) => (
                  <div
                    key={task.id}
                    className={`rounded-md border ${task.is_active ? 'bg-background' : 'bg-muted opacity-60'}`}
                  >
                    {editingTaskId === task.id ? (
                      /* ── Inline edit row ── */
                      <div className="flex items-center gap-2 px-3 py-2 flex-wrap">
                        <Input
                          value={editForm.name}
                          onChange={e => setEditForm({ ...editForm, name: e.target.value })}
                          className="flex-1 h-7 text-sm min-w-[120px]"
                          placeholder="Task name"
                          autoFocus
                        />
                        <Input
                          value={editForm.code}
                          onChange={e => setEditForm({ ...editForm, code: e.target.value.toUpperCase() })}
                          className="w-20 h-7 text-sm font-mono"
                          placeholder="Code"
                        />
                        <Select value={editForm.category || '__none__'} onValueChange={v => setEditForm({ ...editForm, category: v })}>
                          <SelectTrigger className="w-24 h-7 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none__">None</SelectItem>
                            <SelectItem value="CAPS">CAPS</SelectItem>
                            <SelectItem value="OPS">OPS</SelectItem>
                          </SelectContent>
                        </Select>
                        <Button size="sm" className="h-7 px-2 text-xs" onClick={() => handleSaveTaskEdit(task)}>Save</Button>
                        <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => setEditingTaskId(null)}>Cancel</Button>
                      </div>
                    ) : (
                      /* ── Normal display row ── */
                      <div className="flex items-center justify-between px-3 py-2">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm">{task.name}</span>
                          {task.code && (
                            <span className="text-xs bg-muted px-1.5 py-0.5 rounded font-mono">{task.code}</span>
                          )}
                          {task.category ? (
                            <span className={`text-xs px-1.5 py-0.5 rounded font-semibold ${
                              task.category === 'CAPS'
                                ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                                : 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                            }`}>{task.category}</span>
                          ) : (
                            <span className="text-xs text-muted-foreground border border-dashed rounded px-1.5 py-0.5">No type</span>
                          )}
                          {task.is_system && <span className="text-xs text-muted-foreground italic">system</span>}
                          {!task.is_active && <span className="text-xs text-muted-foreground">(inactive)</span>}
                        </div>
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" size="icon" className="h-7 w-7" title="Edit" onClick={() => startEditTask(task)}>
                            <Pencil className="w-3.5 h-3.5" />
                          </Button>
                          <Button
                            variant="ghost" size="icon" className="h-7 w-7"
                            title={task.is_active ? 'Deactivate' : 'Activate'}
                            onClick={() => handleToggleClientTask(task)}
                          >
                            {task.is_active
                              ? <ToggleRight className="w-4 h-4 text-primary" />
                              : <ToggleLeft className="w-4 h-4 text-muted-foreground" />}
                          </Button>
                          {!task.is_system && (
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive"
                              onClick={() => handleDeleteClientTask(task)}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTaskDialogOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Holiday Dialog */}
      <Dialog open={holidayDialogOpen} onOpenChange={setHolidayDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Add Holiday</DialogTitle>
            <DialogDescription>
              Add a holiday or non-working day for {selectedClient?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Date</Label>
              <Input
                type="date"
                value={holidayDateStr}
                onChange={(e) => setHolidayDateStr(e.target.value)}
                data-testid="holiday-date-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Name</Label>
              <Input
                value={holidayName}
                onChange={(e) => setHolidayName(e.target.value)}
                placeholder="e.g., Christmas Day"
                data-testid="holiday-name-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setHolidayDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddHoliday} data-testid="save-holiday-btn">
              Add Holiday
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Manager Dialog */}
      <Dialog open={assignManagerDialogOpen} onOpenChange={setAssignManagerDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserCog className="w-5 h-5" />
              Assign Client Manager
            </DialogTitle>
            <DialogDescription>
              Select a Client Manager for <strong>{assignManagerClient?.name}</strong>.
              They will only be able to manage this client.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Label>Client Manager</Label>
            {clientManagers.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No users with the CLIENT_MANAGER role exist yet. Create one on the Users page first.
              </p>
            ) : (
              <Select value={selectedManagerId} onValueChange={setSelectedManagerId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a manager..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— Unassigned —</SelectItem>
                  {clientManagers.map(u => (
                    <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignManagerDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleAssignManager} disabled={savingManager || clientManagers.length === 0}>
              {savingManager ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
